import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import _, { cloneDeep } from 'lodash'

import {
  Fab, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, Select, TextField, Tooltip,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { employmentRelationshipTypes } from 'utils/constants/DueDiligenceRelations'
import { formatInBrazilianDate, hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'
import AddAttachments from "../../../AddAttachments"

export default function QuestionFour(props) {
  const { questionData, saveResponses, formOwner } = props
  const classes = styles()

  const initialData = {
    bond_type: 'default',
    bond_informed_as_other: '',
    role: '',
    developed_activities: '',
    currently_at_this_job: false,
    start_working_hours: null,
    end_working_hours: null,
    reports_to: '',
    end_of_contract: null,
    investment_advisory_start_time: null,
    investment_advisory_end_time: null,
    is_clt_employee_or_third_party_advisor: false,
    employment_document_attachments: [],
  }

  const initialErrors = {
    bond_type: false,
    bond_informed_as_other: false,
    role: false,
    developed_activities: false,
    currently_at_this_job: false,
    start_working_hours: false,
    end_working_hours: false,
    reports_to: false,
    end_of_contract: false,
    investment_advisory_start_time: false,
    investment_advisory_end_time: false,
    is_clt_employee_or_third_party_advisor: false,
    employment_document_attachments: false,
  }

  const initializeFormState = (data) => {
    return {
      hasAnotherOccupations: data.answer?.items?.length > 0 || false,
      items: data.answer?.items || [],
      employment_document_attachments: data.answer?.employment_document_attachments || [],
      is_clt_employee_or_third_party_advisor: data.answer?.is_clt_employee_or_third_party_advisor || false,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const { value } = event.target
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    if (event.target.name === 'isCltEmployeeOrThirdPartyAdvisor') {
      newFormState.is_clt_employee_or_third_party_advisor = event.target.value === 'yes'
    } else {
      newFormState.hasAnotherOccupations = event.target.value === 'yes'
    }

    setFormState(newFormState)
  }

  const handleStillOnJobRadioChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.currently_at_this_job = event.target.value === 'yes'
    setActivityData(newActivityData)
  }

  const handleSelectChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.bond_type = event.target.value
    setActivityData(newActivityData)
  }

  const hasAnotherOccupations = () => {
    return !(formState.items.length === 0)
  }

  const hasEmploymentDocumentAttachments = () => {
    return !(formState.employment_document_attachments.length === 0)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (formState.items.length > 0 && formState.hasAnotherOccupations === false) {
      toast.warn('Você deve excluir as atividades antes de atualizar as respostas.')
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (!skipValidation) {
      if (formState.hasAnotherOccupations && !hasAnotherOccupations()) {
        toast.error('Ao selecionar "Sim" deve-se adicionar ao menos uma atividade')
        return
      }
      if (formState.is_clt_employee_or_third_party_advisor && !hasEmploymentDocumentAttachments()) {
        toast.error('Ao comunicar se a contratação será CLT ou por meio de Assessor terceirizado,' +
          ' é necessário anexar o documento correspondente.',
        )
      }
    } else {
      newQuestionData.has_answer = true
      const { items, is_clt_employee_or_third_party_advisor, employment_document_attachments } = formState

      newQuestionData.answer = {
        items,
        is_clt_employee_or_third_party_advisor,
        employment_document_attachments: is_clt_employee_or_third_party_advisor ? employment_document_attachments : [],
      }
      setLoading(true)
      await saveResponses(newQuestionData, 'external_activities', !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE)
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (activityData[key] === '' || activityData[key] === null) {
        if (
          activityData.currently_at_this_job === false &&
          (key === 'start_working_hours' || key === 'end_working_hours')
        ) {
          newActivityErrors[key] = false
          return
        }
        if (
          key === 'bond_informed_as_other' &&
          activityData.bond_type !== 'other'
        ) {
          newActivityErrors[key] = false
          return
        }
        newActivityErrors[key] = true
      }
      if (
        key === 'end_of_contract' &&
        (activityData[key] === '' || activityData[key] === null) &&
        activityData.currently_at_this_job
      ) {
        newActivityErrors[key] = false
        return
      }
      if (key === 'bond_type' && activityData[key] === 'default') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveOccupation = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter((activity, index) => index !== position)
    setFormState(newFormState)
  }

  const handleUpdateAttachments = (attachments) => {
    const newFormState = cloneDeep(formState)
    newFormState.employment_document_attachments = attachments
    setFormState(newFormState)
  }

  const buildOccupation = (occupationInfo) => {
    return (<React.Fragment>
      <p>
        <span>Tipos de Vínculo: </span>
        {occupationInfo.bond_type === 'other' ? occupationInfo.bond_informed_as_other : employmentRelationshipTypes[occupationInfo.bond_type]}
      </p>
      <p>
        <span>Cargo: </span>
        {occupationInfo.role}
      </p>

      <p>
        <span>Atividades desempenhadas: </span>
        {occupationInfo.developed_activities}
      </p>
      <p>
        <span>Reporta/reportava-se a: </span>
        {occupationInfo.reports_to}
      </p>
      {occupationInfo.end_of_contract ? (<p>
        <span>Fim do vínculo: </span>
        {formatInBrazilianDate(occupationInfo.end_of_contract)}
      </p>) : null}

      {occupationInfo.currently_at_this_job ? (<p>
        <span>Jornada de trabalho: </span>
        {` ${occupationInfo.start_working_hours} às ${occupationInfo.end_working_hours}.`}
      </p>) : null}
      <p>
        <span>Jornada da atividade de assessoria: </span>
        {` ${occupationInfo.investment_advisory_start_time} às ${occupationInfo.investment_advisory_end_time}.`}
      </p>
    </React.Fragment>)
  }

  useEffect(() => {
    const hasChanged = hasAnswerChanged(formState.items, questionData.answer?.items) ||
      formState.hasAnotherOccupations !== questionData.answer.items?.length > 0 ||
      formState.is_clt_employee_or_third_party_advisor !== questionData.answer.is_clt_employee_or_third_party_advisor ||
      formState.employment_document_attachments?.length !== questionData.answer.employment_document_attachments?.length

    setEdited(hasChanged)
    if (formState.items?.length > 0 && formState.hasAnotherOccupations === false) {
      toast.warn('Você deve excluir as atividades antes de atualizar as respostas.')
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items)) 
      validateAndSave(true)
  }, [formState.items])

  const attachmentsInvalidState = !edited && _.isEmpty(formState.employment_document_attachments) && questionData?.answer_uuid
  return (<div className={classes.root}>
    <div className={classes.formContainer}>
      <h3>Questão 4</h3>
      <div className={classes.form}>
        <FormControl>
          <FormLabel>
            Você exerce alguma atividade externa (remunerada ou não), além das
            que serão prestadas para a XP Inc.?{' '}
          </FormLabel>
          <RadioGroup
            row
            value={formState.hasAnotherOccupations ? 'yes' : 'no'}
            name="hasRelations"
            onChange={handleRadioChange}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Sim" />
            <FormControlLabel value="no" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>

        {formState.hasAnotherOccupations ? (<div className={classes.formFields}>
          <Select
            onChange={handleSelectChange}
            value={activityData.bond_type}
            error={activityErrors.bond_type}
            size="small"
          >
            <MenuItem value="default" disabled>
              Selecione o tipo de vínculo
            </MenuItem>
            {Object.keys(employmentRelationshipTypes).map((key) => (<MenuItem key={key} value={key}>
              {employmentRelationshipTypes[key]}
            </MenuItem>))}
          </Select>

          {activityData.bond_type === 'other' ? (<TextField
            id="bond_informed_as_other"
            label="Tipo de vínculo"
            onChange={handleFieldChange}
            required
            fullWidth
            value={activityData.bond_informed_as_other}
            error={activityErrors.bond_informed_as_other}
            size="small"
          />) : null}

          <TextField
            id="role"
            label="Cargo"
            onChange={handleFieldChange}
            required
            fullWidth
            value={activityData.role}
            error={activityErrors.role}
            size="small"
          />

          <TextField
            id="developed_activities"
            label="Atividades desempenhadas"
            onChange={handleFieldChange}
            required
            fullWidth
            value={activityData.developed_activities}
            error={activityErrors.developed_activities}
            size="small"
          />

          <TextField
            id="reports_to"
            label="A quem se reporta/reportava na estrutura organizacional do órgão?"
            onChange={handleFieldChange}
            error={activityErrors.reports_to}
            value={activityData.reports_to}
            required
            fullWidth
            size="small"
          />
          <FormControl className={classes.radioQuestion}>
            <FormLabel id="">
              Após o credenciamento continuará exercendo essa função?
            </FormLabel>
            <RadioGroup
              row
              value={activityData.currently_at_this_job ? 'yes' : 'no'}
              name="currently_at_this_job"
              onChange={handleStillOnJobRadioChange}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Sim"
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="Não"
              />
            </RadioGroup>
          </FormControl>
          {activityData.currently_at_this_job ? (<div className={classes.times}>
            <FormLabel>Jornada de trabalho:</FormLabel>
            <div>
              <TextField
                id="start_working_hours"
                label="Início"
                type="time"
                onChange={handleFieldChange}
                error={activityErrors.start_working_hours}
                value={activityData.start_working_hours || ''}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />

              <TextField
                id="end_working_hours"
                label="Fim"
                type="time"
                onChange={handleFieldChange}
                error={activityErrors.end_working_hours}
                value={activityData.end_working_hours || ''}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </div>
          </div>) : null}
          {activityData.currently_at_this_job === false ? (<TextField
            id="end_of_contract"
            label="Data em que pretende encerrar o vínculo"
            onChange={handleFieldChange}
            error={activityErrors.end_of_contract}
            value={activityData.end_of_contract || ''}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
          />) : null}

          <div className={classes.times}>
            <FormLabel>Jornada da atividade de assessoria:</FormLabel>
            <div>
              <TextField
                id="investment_advisory_start_time"
                label="Início"
                type="time"
                onChange={handleFieldChange}
                error={activityErrors.investment_advisory_start_time}
                value={activityData.investment_advisory_start_time || ''}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />

              <TextField
                id="investment_advisory_end_time"
                label="Fim"
                type="time"
                onChange={handleFieldChange}
                error={activityErrors.investment_advisory_end_time}
                value={activityData.investment_advisory_end_time || ''}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </div>
          </div>

          <div className={classes.fabContainer}>
            <Tooltip title="Adicionar atividade">
              <Fab
                onClick={handleValidateAndUpdateState}
                disabled={formState?.developed_activities?.length === 10}
                size="small"
                color="primary"
              >
                <Add />
              </Fab>
            </Tooltip>
          </div>
        </div>) : null}

        <FormControl>
          <FormLabel>
            Seu vínculo com o escritório será de celetista (CLT) ou assessor terceirizado?
          </FormLabel>
          <RadioGroup
            row
            value={formState.is_clt_employee_or_third_party_advisor ? 'yes' : 'no'}
            name="isCltEmployeeOrThirdPartyAdvisor"
            onChange={handleRadioChange}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Sim" />
            <FormControlLabel value="no" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>

        {formState.is_clt_employee_or_third_party_advisor && <AddAttachments
          targetFilename={`${formOwner?.prospect?.person?.cpf}-Documento_trabalhista`}
          title="Carteira de trabalho (CLT) ou Contrato de trabalho (Assessor terceirizado):"
          attachments={formState.employment_document_attachments}
          updateAttachments={handleUpdateAttachments}
          answerId={questionData?.answer_uuid}
          error={activityErrors.employment_document_attachments || attachmentsInvalidState}
        />}

      </div>
      {formState?.items?.length > 0 ? (<div className={classes.answersContainer}>
        {addKeyToObjectsArray(formState.items)?.map((activity, index) => (<AnswerCard
          key={activity.key}
          index={index}
          handleRemove={handleRemoveOccupation}
          mustRemove={formState.hasAnotherOccupations === false}
        >
          {buildOccupation(activity)}
        </AnswerCard>))}
      </div>) : null}
    </div>
    <div className={classes.buttonsContainer}>
      <QuestionButton
        variant="contained"
        onClick={validateAndSave}
        loading={loading}
        saved={saved}
        edited={edited}
        disabled={(formState.hasAnotherOccupations && formState.items.length === 0) || (saved && !edited)}
      />
    </div>
  </div>)
}
